import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import {
  GraduationCap, ChartLineUp, ArrowRight, Book, Spinner, Sparkle, CaretRight,
} from "@phosphor-icons/react";

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [selected, setSelected] = useState(null); // null | "beginner" | "expert"
  const [saving, setSaving] = useState(false);

  const pick = async (level) => {
    if (saving) return;
    setSelected(level);
    setSaving(true);
    try {
      await axios.post(`${API}/auth/experience-level`, { level }, { withCredentials: true });
      await checkAuth();
    } catch (e) {
      // Non-blocking — still show the guidance panel
      console.error("Failed to save level:", e);
    } finally {
      setSaving(false);
    }
  };

  const skipToDashboard = () => navigate("/");

  const firstName = (user && user.name) ? user.name.split(" ")[0] : null;

  return (
    <div className="min-h-[85vh] flex items-start sm:items-center justify-center px-4 py-8" data-testid="onboarding-page">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-amber-500/10 border border-amber-500/30 mb-4">
            <Sparkle size={22} weight="fill" className="text-amber-400" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight">
            Welcome to The Golden Nest{firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="text-xs sm:text-sm text-zinc-500 mt-2 max-w-xl mx-auto">
            Before we get started, help us tailor your experience. How familiar are you with tracking crypto &amp; market data?
          </p>
        </div>

        {/* Choice cards */}
        {!selected && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="onboarding-choices">
            <button
              onClick={() => pick("beginner")}
              disabled={saving}
              data-testid="choose-beginner"
              className="text-left p-5 sm:p-6 rounded-sm border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/50 hover:bg-zinc-900/70 transition-colors group disabled:opacity-50"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                  <GraduationCap size={20} weight="bold" className="text-emerald-400" />
                </div>
                <h2 className="font-heading text-lg font-bold text-zinc-100">Beginner</h2>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                I'm new to crypto &amp; market data. Show me the fundamentals first so the charts, indicators, and news make sense.
              </p>
              <div className="flex items-center gap-1 mt-4 text-[11px] text-amber-400/80 font-medium group-hover:text-amber-300">
                Start learning <ArrowRight size={12} weight="bold" />
              </div>
            </button>

            <button
              onClick={() => pick("expert")}
              disabled={saving}
              data-testid="choose-expert"
              className="text-left p-5 sm:p-6 rounded-sm border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/50 hover:bg-zinc-900/70 transition-colors group disabled:opacity-50"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-sm bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <ChartLineUp size={20} weight="bold" className="text-blue-400" />
                </div>
                <h2 className="font-heading text-lg font-bold text-zinc-100">Expert</h2>
              </div>
              <p className="text-xs text-zinc-400 leading-relaxed">
                I already understand indicators, on-chain data, and market macro. Drop me straight into the live dashboard.
              </p>
              <div className="flex items-center gap-1 mt-4 text-[11px] text-amber-400/80 font-medium group-hover:text-amber-300">
                Go to dashboard <ArrowRight size={12} weight="bold" />
              </div>
            </button>
          </div>
        )}

        {/* Beginner guidance */}
        {selected === "beginner" && (
          <div className="rounded-sm border border-emerald-500/30 bg-zinc-900/60 p-5 sm:p-6 space-y-5" data-testid="beginner-guidance">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
                <GraduationCap size={20} weight="bold" className="text-emerald-400" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold text-zinc-100">Great — let's build your foundation</h2>
                <p className="text-[11px] text-zinc-500 mt-0.5">We recommend starting here before exploring live data.</p>
              </div>
            </div>

            <p className="text-sm text-zinc-300 leading-relaxed">
              It's best to spend some time in the <span className="text-amber-300 font-medium">Crypto Academy</span> and
              {" "}<span className="text-amber-300 font-medium">Financial Education</span> tabs. These will teach you the
              building blocks — wallets, blockchains, compounding, budgeting, leverage, and risk — so when you look at the
              dashboard later, everything clicks.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate("/academy")}
                data-testid="go-crypto-academy"
                className="flex items-center justify-between gap-2 p-4 rounded-sm border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Book size={18} weight="bold" className="text-amber-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-100">Crypto Academy</p>
                    <p className="text-[10px] text-amber-300/70">Blockchains, wallets, DeFi fundamentals</p>
                  </div>
                </div>
                <CaretRight size={14} className="text-amber-300/70 shrink-0" />
              </button>

              <button
                onClick={() => navigate("/financial-education?tab=money")}
                data-testid="go-financial-education"
                className="flex items-center justify-between gap-2 p-4 rounded-sm border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <GraduationCap size={18} weight="bold" className="text-amber-400 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-100">Financial Education</p>
                    <p className="text-[10px] text-amber-300/70">Start with Money basics, then budgeting, trusts &amp; more</p>
                  </div>
                </div>
                <CaretRight size={14} className="text-amber-300/70 shrink-0" />
              </button>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-zinc-800/60">
              <p className="text-[11px] text-zinc-500">You can always revisit these tabs any time from the main nav.</p>
              <button
                onClick={skipToDashboard}
                data-testid="skip-to-dashboard"
                className="text-[11px] text-zinc-400 hover:text-zinc-200 transition-colors flex items-center gap-1"
              >
                Skip to dashboard <ArrowRight size={11} />
              </button>
            </div>
          </div>
        )}

        {/* Expert redirect feedback */}
        {selected === "expert" && (
          <div className="rounded-sm border border-blue-500/30 bg-zinc-900/60 p-6 text-center space-y-4" data-testid="expert-confirmation">
            <div className="inline-flex items-center justify-center w-10 h-10 rounded-sm bg-blue-500/10 border border-blue-500/30">
              <ChartLineUp size={20} weight="bold" className="text-blue-400" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-bold text-zinc-100">You're all set</h2>
              <p className="text-xs text-zinc-500 mt-1">Heading to the live dashboard — markets, watchlist, and trending data.</p>
            </div>
            <button
              onClick={skipToDashboard}
              data-testid="expert-continue-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 transition-colors text-sm font-medium"
            >
              {saving ? <Spinner size={14} className="animate-spin" /> : null}
              Go to Dashboard <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
